import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import FaqSection from './sections/FaqSection';
import ServicesSection from './sections/ServicesSection';
import TopBrandsSection from 'pages/customer-join/sections/TopBrandsSection';
import FormSection from 'pages/customer-join/sections/formSection/FormSection';
import HowToJoinSection from 'pages/customer-join/sections/HowToJoinSection';
import SavingsSection from 'pages/customer-join/sections/SavingSection/SavingsSection';
import BenefitsSection from 'pages/customer-join/sections/BenefitsSection';
import FixedButton from './sections/FixedButton';
import { useGetCustomerJoinDataQuery } from 'redux/api/content.api';
import Page404 from 'containers/page404';
import Loader from 'components/common/loader';
import HeaderGallery from './sections/HeaderGallery';

const BUFFER_PIXELS = 0; // Buffer zone to show button earlier

const CustomerJoin = ({match}) => {
  const {pageName} = match.params;
  const { data: customerJoinData, isLoading, error } = useGetCustomerJoinDataQuery({pageName});
  const [formPosition, setFormPosition] = useState({ above: false, below: true });
  const [afterFormPosition, setAfterFormPosition] = useState({ above: false, below: true });
  const formSectionRef = useRef(null);
  const afterFormRef = useRef(null);


  useEffect(() => {
    const formObserver = new IntersectionObserver(
      ([entry]) => {
        const bounds = entry.boundingClientRect;
        const windowHeight = window.innerHeight;
        
        setFormPosition({
          above: bounds.bottom < -BUFFER_PIXELS, // Add buffer for earlier appearance when scrolling up
          below: bounds.top > (windowHeight + BUFFER_PIXELS) // Add buffer for earlier appearance when scrolling down
        });
      },
      {
        threshold: [0, 1]
      }
    );

    const afterFormObserver = new IntersectionObserver(
      ([entry]) => {
        const bounds = entry.boundingClientRect;
        const windowHeight = window.innerHeight;

        setAfterFormPosition({
          above: bounds.bottom < -BUFFER_PIXELS, // Add buffer for earlier appearance when scrolling up
          below: bounds.top > (windowHeight + BUFFER_PIXELS) // Add buffer for earlier appearance when scrolling down
        });
      },
      {
        threshold: [0, 1]
      }
    );

    if (formSectionRef.current) {
      formObserver.observe(formSectionRef.current);
    }
    if (afterFormRef.current) {
      afterFormObserver.observe(afterFormRef.current);
    }

    return () => {
      if (formSectionRef.current) {
        formObserver.unobserve(formSectionRef.current);
      }
      if (afterFormRef.current) {
        afterFormObserver.unobserve(afterFormRef.current);
      }
    };
  }, []);

  const shouldShowButton = (
    (formPosition.above && afterFormPosition.above) || 
    (formPosition.below && afterFormPosition.below)
  );

  const handleFixedButtonClick = () => {
    if (formSectionRef.current) {
      const targetPosition = formSectionRef.current.offsetTop -120;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Loader />
      </Box>
    );
  }
  if( (error || customerJoinData.error )&& pageName) {
    // TODO: redirect to customer join page
     return (
      <Page404 />
    );     
   }

  if (error) {
    return (
      <Page404 />
    );
  }

  if (!customerJoinData) {
    return null;
  }


  const {savingSection, galleryHeaderSection, benefitsSection, topBrandsSection, servicesSection, faqSection, howToJoinSection } = customerJoinData

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        maxWidth: '100%',
      }}
      testID="customer-join"
    > 
      {shouldShowButton && (
        <FixedButton 
          onClick={handleFixedButtonClick}
          testID="fixed-button"
        />
      )}
      {galleryHeaderSection && <HeaderGallery data={galleryHeaderSection} onClick={handleFixedButtonClick} testID="header-gallery-section" />}
      {benefitsSection && <BenefitsSection benefitsData={benefitsSection} testID="benefits-section" />}
      {savingSection && <SavingsSection data={savingSection} joinButtonText={customerJoinData.joinButtonText} onClick={handleFixedButtonClick} testID="saving-section" />}
      {howToJoinSection && <HowToJoinSection data={customerJoinData.howToJoinSection} joinButtonText={customerJoinData.joinButtonText} onClick={handleFixedButtonClick} testID="how-to-join-section" />}
      <div ref={formSectionRef} id="form-section" testid="form-section"/>
      <FormSection data={customerJoinData.formSection} testID="form-section-component" />
      <div  ref={afterFormRef} testid="after-form-section"/>
      {topBrandsSection && <TopBrandsSection  data={customerJoinData.topBrandsSection} testID="top-brands-section" />}
      {servicesSection && <ServicesSection data={customerJoinData.servicesSection} testID="services-section" />}
      {faqSection && <FaqSection data={customerJoinData.faqSection} testID="faq-section" />}
    </Box>
  );
};

export default CustomerJoin; 